import React, { useEffect, useRef, useState } from "react";
import { NavLink, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { Autocomplete, GoogleMap, Marker, useJsApiLoader } from "@react-google-maps/api";
import {
  FiAlignLeft,
  FiArrowLeft,
  FiChevronRight,
  FiHome,
  FiImage,
  FiMapPin,
  FiMaximize2,
  FiNavigation,
  FiUploadCloud,
  FiX,
} from "react-icons/fi";
import { MdHeight, MdOutlineWarehouse, MdStraighten, MdSwapHoriz } from "react-icons/md";
import { RoutesPaths } from "../Router/constant";
import { uploadSingleFile } from "../Services/userService";
import {
  createWarehouse,
  extractWarehouse,
  getApiMessage,
  getWarehouseById,
  updateWarehouse,
} from "../Services/warehouseService";
import Skeleton from "../Components/SkeletonLoader";
import AdminOnly from "../Components/AdminOnly";
import { warehouseImageUrl } from "./Warehouse";

// Same key and loader options as the order form (OrderForm.js) so the Maps script is shared.
const GOOGLE_MAPS_API_KEY = "AIzaSyBk6NKU8aUcbaQgqNJlGgh9ARAG6q5PkrA";
const libraries = ["places"];
const MAX_IMAGE_BYTES = 5 * 1024 * 1024;
const DEFAULT_CENTER = { lat: -26.2041, lng: 28.0473 };
const mapContainerStyle = { width: "100%", height: "260px", borderRadius: "12px" };

const emptyForm = {
  name: "",
  description: "",
  length: "",
  width: "",
  height: "",
  location: "",
  latitude: "",
  longitude: "",
};

const toNumberOrEmpty = (value) => (value === "" || value === null || value === undefined ? "" : String(value));

function WarehouseFormPage() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const autocompleteRef = useRef(null);
  const fileInputRef = useRef(null);

  const [form, setForm] = useState(emptyForm);
  const [images, setImages] = useState([]); // uploaded file names
  const [uploading, setUploading] = useState(0);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(isEdit);
  const [loadError, setLoadError] = useState(false);
  const [saving, setSaving] = useState(false);

  const { isLoaded } = useJsApiLoader({ googleMapsApiKey: GOOGLE_MAPS_API_KEY, libraries });

  /* ---------- load (edit) ---------- */
  useEffect(() => {
    if (!isEdit) return;

    const load = async () => {
      setLoading(true);
      setLoadError(false);
      try {
        const warehouse = extractWarehouse(await getWarehouseById(id));
        if (!warehouse) throw new Error("Warehouse not found");

        setForm({
          name: warehouse.name || "",
          description: warehouse.description || "",
          length: toNumberOrEmpty(warehouse.dimensions?.length),
          width: toNumberOrEmpty(warehouse.dimensions?.width),
          height: toNumberOrEmpty(warehouse.dimensions?.height),
          location: warehouse.location || "",
          latitude: toNumberOrEmpty(warehouse.latitude),
          longitude: toNumberOrEmpty(warehouse.longitude),
        });
        setImages(Array.isArray(warehouse.uploadImages) ? warehouse.uploadImages : []);
      } catch (error) {
        console.error("Load warehouse error:", error);
        setLoadError(true);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [id, isEdit]);

  /* ---------- helpers ---------- */
  const update = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    if (errors[key]) setErrors((prev) => ({ ...prev, [key]: "" }));
  };

  const handleNumber = (key) => (e) => {
    // digits and a single decimal point
    const value = e.target.value.replace(/[^0-9.]/g, "").replace(/(\..*)\./g, "$1");
    update(key, value);
  };

  const handleCoordinate = (key) => (e) => {
    const value = e.target.value.replace(/[^0-9.-]/g, "");
    update(key, value);
  };

  const position =
    form.latitude !== "" && form.longitude !== "" && !isNaN(Number(form.latitude)) && !isNaN(Number(form.longitude))
      ? { lat: Number(form.latitude), lng: Number(form.longitude) }
      : null;

  const reverseGeocode = (lat, lng) =>
    new Promise((resolve, reject) => {
      if (!window.google?.maps) return reject(new Error("Maps not loaded"));
      new window.google.maps.Geocoder().geocode({ location: { lat, lng } }, (results, status) => {
        if (status === "OK" && results[0]) resolve(results[0].formatted_address);
        else reject(new Error("No address found"));
      });
    });

  const setPin = async (lat, lng, { fillAddress = true } = {}) => {
    setForm((prev) => ({ ...prev, latitude: lat.toFixed(6), longitude: lng.toFixed(6) }));
    setErrors((prev) => ({ ...prev, latitude: "", longitude: "", location: fillAddress ? "" : prev.location }));
    if (!fillAddress) return;
    try {
      const address = await reverseGeocode(lat, lng);
      setForm((prev) => ({ ...prev, location: address }));
    } catch (err) {
      console.warn(err);
    }
  };

  const handleMapPoint = (e) => setPin(e.latLng.lat(), e.latLng.lng());

  const handlePlaceChanged = () => {
    const place = autocompleteRef.current?.getPlace();
    if (!place?.geometry) return;
    const lat = place.geometry.location.lat();
    const lng = place.geometry.location.lng();
    setForm((prev) => ({
      ...prev,
      location: place.formatted_address || prev.location,
      latitude: lat.toFixed(6),
      longitude: lng.toFixed(6),
    }));
    setErrors((prev) => ({ ...prev, location: "", latitude: "", longitude: "" }));
  };

  const locateMe = () => {
    if (!navigator.geolocation) {
      toast.error("Location is not supported by this browser");
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => setPin(pos.coords.latitude, pos.coords.longitude),
      () => toast.error("Unable to get your current location"),
    );
  };

  /* ---------- images ---------- */
  const handleImages = async (event) => {
    const files = Array.from(event.target.files || []);
    event.target.value = "";
    if (!files.length) return;

    const valid = files.filter((file) => {
      if (!file.type.startsWith("image/")) {
        toast.error(`${file.name} is not an image`);
        return false;
      }
      if (file.size > MAX_IMAGE_BYTES) {
        toast.error(`${file.name} is larger than 5MB`);
        return false;
      }
      return true;
    });

    for (const file of valid) {
      setUploading((n) => n + 1);
      try {
        const base64 = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });
        const response = await uploadSingleFile({ base64 });
        const fileName = response?.data?.data?.fileName;
        if (!fileName) throw new Error("No file name returned");
        setImages((prev) => [...prev, fileName]);
      } catch (error) {
        console.error("Image upload error:", error);
        toast.error(`Unable to upload ${file.name}`);
      } finally {
        setUploading((n) => n - 1);
      }
    }
  };

  const removeImage = (fileName) => setImages((prev) => prev.filter((f) => f !== fileName));

  /* ---------- submit ---------- */
  const validate = () => {
    const next = {};
    if (!form.name.trim()) next.name = "Please enter warehouse name.";
    ["length", "width", "height"].forEach((key) => {
      if (form[key] === "") next[key] = "Required";
      else if (isNaN(Number(form[key])) || Number(form[key]) <= 0) next[key] = "Must be greater than 0";
    });
    if (!form.location.trim()) next.location = "Please enter or pick a location.";
    const lat = Number(form.latitude);
    const lng = Number(form.longitude);
    if (form.latitude === "" || isNaN(lat) || lat < -90 || lat > 90) next.latitude = "Latitude must be between -90 and 90.";
    if (form.longitude === "" || isNaN(lng) || lng < -180 || lng > 180) next.longitude = "Longitude must be between -180 and 180.";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (saving) return;
    if (uploading > 0) {
      toast.info("Please wait for the images to finish uploading.");
      return;
    }
    if (!validate()) {
      toast.error("Please fix the highlighted fields.");
      return;
    }

    const payload = {
      name: form.name.trim(),
      description: form.description.trim(),
      dimensions: {
        length: Number(form.length),
        width: Number(form.width),
        height: Number(form.height),
      },
      uploadImages: images,
      location: form.location.trim(),
      latitude: Number(form.latitude),
      longitude: Number(form.longitude),
    };

    setSaving(true);
    try {
      const response = isEdit ? await updateWarehouse(id, payload) : await createWarehouse(payload);
      if (response?.data?.status === false || response?.data?.success === false) {
        throw new Error(getApiMessage(response, "Unable to save warehouse"));
      }
      toast.success(
        getApiMessage(response, isEdit ? "Warehouse updated successfully" : "Warehouse created successfully"),
      );
      navigate(RoutesPaths.WAREHOUSE);
    } catch (error) {
      console.error("Save warehouse error:", error);
      toast.error(getApiMessage(error, error?.message || "Unable to save warehouse"));
    } finally {
      setSaving(false);
    }
  };

  /* ---------- render helpers ---------- */
  const renderField = ({ field, label, required, Icon, children, className = "" }) => (
    <div id={`field-${field}`} className={`qv-field ${errors[field] ? "has-error" : ""} ${className}`}>
      <span className="qv-field-icon">
        <Icon />
      </span>
      <div className="qv-field-body">
        <label className="qv-label" htmlFor={`wh-${field}`}>
          {label}
          {required && <span className="qv-required"> *</span>}
        </label>
        {loading ? <Skeleton width="100%" height={40} /> : children}
        {errors[field] && <p className="m-0 qv-error">{errors[field]}</p>}
      </div>
    </div>
  );

  const title = isEdit ? "Edit warehouse" : "Create warehouse";

  if (loadError) {
    return (
      <div className="container-fluid qd-page">
        <div className="qd-card qd-panel">
          <div className="qu-empty">
            <span className="qu-empty-art">
              <MdOutlineWarehouse />
            </span>
            <h6>Unable to load this warehouse</h6>
            <p>It may have been removed, or there was a problem loading it.</p>
            <NavLink to={RoutesPaths.WAREHOUSE} className="qd-btn qd-btn-primary">
              <FiArrowLeft /> Back to warehouses
            </NavLink>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid qd-page qv-page">
      <nav className="qd-breadcrumb">
        <NavLink to={RoutesPaths.DASHBOARD} aria-label="Dashboard">
          <FiHome />
        </NavLink>
        <FiChevronRight />
        <NavLink to={RoutesPaths.WAREHOUSE}>Warehouse</NavLink>
        <FiChevronRight />
        <span className="qd-breadcrumb-current">{title}</span>
      </nav>

      <div className="qd-page-header">
        <div>
          <h1 className="qd-title">{title}</h1>
          <p className="qd-subtitle">
            {isEdit
              ? "Update the warehouse details, size, photos and location."
              : "Add a storage location with its size, photos and map position."}
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} noValidate>
        {/* Details */}
        <section className="qd-card qv-section">
          <div className="qv-section-head">
            <span className="qv-section-icon">
              <MdOutlineWarehouse />
            </span>
            <div>
              <h6>Warehouse details</h6>
              <p>Name and a short description of the warehouse.</p>
            </div>
          </div>
          <div className="qv-grid">
            {renderField({
              field: "name",
              label: "Warehouse name",
              required: true,
              Icon: MdOutlineWarehouse,
              children: (
                <input
                  id="wh-name"
                  className="qv-input"
                  placeholder="e.g. Johannesburg Main Warehouse"
                  value={form.name}
                  onChange={(e) => update("name", e.target.value)}
                />
              ),
            })}
            {renderField({
              field: "description",
              label: "Description",
              Icon: FiAlignLeft,
              children: (
                <textarea
                  id="wh-description"
                  className="qv-input qw-textarea"
                  placeholder="e.g. Primary warehouse for storage."
                  value={form.description}
                  onChange={(e) => update("description", e.target.value)}
                  rows={3}
                />
              ),
            })}
          </div>
        </section>

        {/* Dimensions */}
        <section className="qd-card qv-section">
          <div className="qv-section-head">
            <span className="qv-section-icon">
              <FiMaximize2 />
            </span>
            <div>
              <h6>Dimensions</h6>
              <p>Inside measurements of the storage area, in metres.</p>
            </div>
          </div>
          <div className="qw-dim-grid">
            {[
              { key: "length", label: "Length (m)", Icon: MdStraighten, placeholder: "e.g. 50" },
              { key: "width", label: "Width (m)", Icon: MdSwapHoriz, placeholder: "e.g. 30" },
              { key: "height", label: "Height (m)", Icon: MdHeight, placeholder: "e.g. 12" },
            ].map(({ key, label, Icon, placeholder }) =>
              renderField({
                field: key,
                label,
                required: true,
                Icon,
                children: (
                  <input
                    id={`wh-${key}`}
                    className="qv-input"
                    inputMode="decimal"
                    placeholder={placeholder}
                    value={form[key]}
                    onChange={handleNumber(key)}
                  />
                ),
              }),
            )}
          </div>
          {!loading && form.length && form.width && form.height && (
            <p className="qw-dim-total">
              Floor area <strong>{(Number(form.length) * Number(form.width)).toLocaleString()} m²</strong> · Volume{" "}
              <strong>
                {(Number(form.length) * Number(form.width) * Number(form.height)).toLocaleString()} m³
              </strong>
            </p>
          )}
        </section>

        {/* Images */}
        <section className="qd-card qv-section">
          <div className="qv-section-head">
            <span className="qv-section-icon">
              <FiImage />
            </span>
            <div>
              <h6>Photos</h6>
              <p>Upload photos of the warehouse. The first photo is used as the cover.</p>
            </div>
          </div>
          <div className="qw-images">
            {images.map((fileName, index) => (
              <div className="qw-image" key={fileName}>
                <img src={warehouseImageUrl(fileName)} alt={`Warehouse ${index + 1}`} />
                {index === 0 && <span className="qw-cover">Cover</span>}
                <button
                  type="button"
                  className="qw-image-remove"
                  onClick={() => removeImage(fileName)}
                  aria-label={`Remove photo ${index + 1}`}
                >
                  <FiX />
                </button>
              </div>
            ))}
            {[...Array(uploading)].map((_, i) => (
              <div className="qw-image qw-image-loading" key={`up-${i}`}>
                <span className="qm-spinner" aria-hidden="true" />
              </div>
            ))}
            <button
              type="button"
              className="qw-add-image"
              onClick={() => fileInputRef.current?.click()}
              disabled={loading}
            >
              <FiUploadCloud />
              <strong>Upload photos</strong>
              <small>JPG, PNG · Max 5MB each</small>
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              hidden
              onChange={handleImages}
            />
          </div>
        </section>

        {/* Location */}
        <section className="qd-card qv-section">
          <div className="qv-section-head">
            <span className="qv-section-icon">
              <FiMapPin />
            </span>
            <div>
              <h6>Location</h6>
              <p>Search for the address, or click the map / drag the pin to set the exact spot.</p>
            </div>
          </div>

          <div className="qw-location">
            <div className="qw-location-fields">
              {renderField({
                field: "location",
                label: "Address",
                required: true,
                Icon: FiMapPin,
                children: isLoaded ? (
                  <Autocomplete onLoad={(ref) => (autocompleteRef.current = ref)} onPlaceChanged={handlePlaceChanged}>
                    <input
                      id="wh-location"
                      className="qv-input"
                      placeholder="Search for an address"
                      value={form.location}
                      onChange={(e) => update("location", e.target.value)}
                    />
                  </Autocomplete>
                ) : (
                  <input
                    id="wh-location"
                    className="qv-input"
                    placeholder="Enter the address"
                    value={form.location}
                    onChange={(e) => update("location", e.target.value)}
                  />
                ),
              })}
              <div className="qw-coords">
                {renderField({
                  field: "latitude",
                  label: "Latitude",
                  required: true,
                  Icon: FiNavigation,
                  children: (
                    <input
                      id="wh-latitude"
                      className="qv-input"
                      inputMode="decimal"
                      placeholder="-26.2041"
                      value={form.latitude}
                      onChange={handleCoordinate("latitude")}
                    />
                  ),
                })}
                {renderField({
                  field: "longitude",
                  label: "Longitude",
                  required: true,
                  Icon: FiNavigation,
                  children: (
                    <input
                      id="wh-longitude"
                      className="qv-input"
                      inputMode="decimal"
                      placeholder="28.0473"
                      value={form.longitude}
                      onChange={handleCoordinate("longitude")}
                    />
                  ),
                })}
              </div>
              <button
                type="button"
                className="qd-btn qd-btn-outline qd-btn-sm qw-locate"
                onClick={locateMe}
                disabled={!isLoaded}
              >
                <FiNavigation /> Use my current location
              </button>
            </div>

            <div className="qw-map">
              {isLoaded ? (
                <GoogleMap
                  mapContainerStyle={mapContainerStyle}
                  center={position || DEFAULT_CENTER}
                  zoom={position ? 14 : 5}
                  onClick={handleMapPoint}
                >
                  {position && <Marker position={position} draggable onDragEnd={handleMapPoint} />}
                </GoogleMap>
              ) : (
                <div className="qo-map-loading" style={{ height: 260 }}>
                  Loading map...
                </div>
              )}
            </div>
          </div>
        </section>

        <div className="qv-actions">
          <NavLink to={RoutesPaths.WAREHOUSE} className="qd-btn qv-cancel-btn">
            Cancel
          </NavLink>
          <button
            type="submit"
            className="qd-btn qd-btn-primary qv-submit"
            disabled={saving || loading || uploading > 0}
          >
            {saving ? (
              <>
                <span className="qo-spinner" /> Saving...
              </>
            ) : uploading > 0 ? (
              "Uploading photos..."
            ) : isEdit ? (
              "Save changes"
            ) : (
              "Create warehouse"
            )}
          </button>
        </div>
      </form>
    </div>
  );
}

export default function WarehouseForm() {
  return (
    <AdminOnly>
      <WarehouseFormPage />
    </AdminOnly>
  );
}
